import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductList from '../../components/products/ProductList';
import GrainDropdown from '../../components/products/GrainDropdown';
import useProducts from '../../hooks/useProducts';
import { useCart } from '../../context/CartContext';
import useRequireCompleteProfile from '../../hooks/useRequireCompleteProfile';
import { PRODUCT_CATEGORIES } from '../../utils/constants';
import './Products.css';

const Products = () => {
  const navigate = useNavigate();
  useRequireCompleteProfile();
  const { products, loading, error } = useProducts();
  const { items: orderItems, total } = useCart();
  const totalUnits = orderItems.reduce((s, i) => s + i.quantity, 0);

  const [activeCategory, setActiveCategory] = useState(null);
  const [activeGrain, setActiveGrain] = useState('');

  // Derive available categories and grain options from loaded products
  const presentCategoryKeys = new Set(products.flatMap((p) => (p.categories || []).map((c) => c.key)));
  const availableCategories = PRODUCT_CATEGORIES.filter((c) => presentCategoryKeys.has(c.key));

  const grainMap = new Map();
  products.forEach((p) => {
    (p.grain_varieties || []).forEach((g) => grainMap.set(g.id, g.name));
  });
  const grainOptions = [...grainMap.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([id, name]) => ({ id, name }));

  const filteredProducts = products.filter((p) => {
    const matchesCategory = !activeCategory || (p.categories || []).some((c) => c.key === activeCategory);
    const matchesGrain = !activeGrain || (p.grain_varieties || []).some((g) => String(g.id) === activeGrain);
    return matchesCategory && matchesGrain;
  });

  const showFilters = !loading && !error && (availableCategories.length > 0 || grainOptions.length > 0);

  return (
    <div className={`products-page${orderItems.length > 0 ? ' products-page--has-fab' : ''}`}>
      <div className="products-page__header">
        <p className="products-page__eyebrow">Deep Roots Milling</p>
        <h1 className="products-page__title">Our Products</h1>
      </div>

      <hr className="products-page__rule" />

      {showFilters && (
        <div className="products-page__filters">
          {availableCategories.length > 0 && (
            <div className="products-page__filter-pills">
              <button
                type="button"
                className={`pp-filter-btn ${!activeCategory ? 'pp-filter-btn--active' : ''}`}
                onClick={() => setActiveCategory(null)}
              >
                All
              </button>
              {availableCategories.map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  className={`pp-filter-btn ${activeCategory === cat.key ? 'pp-filter-btn--active' : ''}`}
                  onClick={() => setActiveCategory(cat.key)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}
          {grainOptions.length > 0 && (
            <div className="products-page__grain-wrap">
              <GrainDropdown
                options={grainOptions}
                value={activeGrain}
                onChange={setActiveGrain}
                classPrefix="pp-grain"
              />
            </div>
          )}
        </div>
      )}

      <div className="products-page__content">
        <ProductList
          products={filteredProducts}
          loading={loading}
          error={error}
        />
      </div>

      {orderItems.length > 0 && (
        <button
          className="products-page__cart-fab"
          onClick={() => navigate('/order/new')}
        >
          <span className="products-page__cart-fab-badge">{totalUnits}</span>
          <span className="products-page__cart-fab-label">
            {totalUnits === 1 ? 'item' : 'items'}
          </span>
          <span className="products-page__cart-fab-sep">·</span>
          <span className="products-page__cart-fab-total">${total.toFixed(2)}</span>
          <span className="products-page__cart-fab-arrow">→</span>
        </button>
      )}
    </div>
  );
};

export default Products;
